import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

const translations = {
  en: {
    // Navbar
    'nav.home': 'Home',
    'nav.vision': 'Vision',
    'nav.programs': 'Programs',
    'nav.summer': 'Summer School',
    'nav.perspectives': 'Perspectives',
    'nav.certificates': 'Certificates',
    'nav.educators': 'Our Team',
    'nav.contribute': 'Contribute',
    'nav.signin': 'Sign In',
    'nav.dashboard': 'My Dashboard',
    'nav.admin': 'Admin Dashboard',
    'nav.signout': 'Sign Out',

    // Landing
    'landing.welcome.title': 'Welcome back',
    'landing.welcome.subtitle': 'Glad to see you again at OFA Academy™.',
    'landing.badge': 'OFA Academy™',
    'landing.online.pill': '100% Online · Worldwide access',
    'landing.hero.title': 'A global academy for a changing world.',
    'landing.hero.subtitle': 'A fully online academy — learning beyond borders, disciplines, and life stages. An independent global institute for students, educators, and lifelong learners.',
    'landing.hero.cta': 'Explore Programs',
    'landing.hero.vision': 'Our Vision',
    'landing.about.badge': 'About the Academy',
    'landing.about.title': 'More than a school. A global intellectual space.',
    'landing.about.p1': 'OFA Academy™ is a new learning structure connected to OFALycée — the international French-American online school — but designed as an independent global academy.',
    'landing.about.p2': 'It is not a traditional school and not based on yearly enrollment. It offers flexible, high-level learning experiences combining humanities, languages, AI, and international education for a global audience.',
    'landing.about.link': 'Read our philosophy',
    'landing.about.year': 'Est. 2026',
    'landing.about.label': 'Global Institute',
    'landing.video.badge': 'Featured Insight',
    'landing.video.title': 'The future of learning is here.',
    'landing.video.subtitle': 'Watch how technology and human connection are transforming education for the next generation.',
    'landing.video.quote': 'AI represents probably the biggest positive transformation that education has ever seen.',
    'landing.video.description': 'At OFA Academy™, we embrace AI as a partner in learning — amplifying human potential while preserving the deep thinking and cultural richness that define true education.',
    'landing.video.learnMore': 'Explore our vision',
    'landing.audience.badge': 'Who It\'s For',
    'landing.audience.title': 'Designed for curious minds at every stage.',
    'landing.audience.students': 'Students',
    'landing.audience.students.desc': 'Middle school to university-prep learners seeking global academic excellence.',
    'landing.audience.educators': 'Educators',
    'landing.audience.educators.desc': 'Teachers and school leaders advancing their practice through research-driven programs.',
    'landing.audience.adults': 'Adults',
    'landing.audience.adults.desc': 'Lifelong learners exploring humanities, AI, and international perspectives.',
    'landing.audience.families': 'International Families',
    'landing.audience.families.desc': 'Families navigating bilingual education and cross-cultural academic pathways.',
    'landing.pillars.badge': 'Learning Pillars',
    'landing.pillars.title': 'Where tradition meets the future.',
    'landing.pillars.humanities': 'Humanities',
    'landing.pillars.humanities.desc': 'Critical thinking, philosophy, literature, and cultural studies.',
    'landing.pillars.languages': 'Languages',
    'landing.pillars.languages.desc': 'Bilingual excellence in French and English for global communication.',
    'landing.pillars.ai': 'AI & Future Skills',
    'landing.pillars.ai.desc': 'Understanding artificial intelligence and its role in shaping education.',
    'landing.pillars.formation': 'Intellectual Formation',
    'landing.pillars.formation.desc': 'Deep learning that fosters independence, creativity, and rigor.',
    'landing.cta.title': 'Begin your learning journey.',
    'landing.cta.subtitle': 'Explore our catalog of programs designed for a global audience. Flexible formats, premium instruction, meaningful outcomes.',
    'landing.cta.explore': 'Explore Programs',
    'landing.cta.teach': 'Teach with Us',

    // Vision
    'vision.badge': 'Vision & Philosophy',
    'vision.title': 'Education must evolve.',
    'vision.subtitle': 'The world has changed. The way we learn must change with it. OFA Academy™ was created from a conviction: that education can be rigorous, flexible, and globally meaningful — all at once.',
    'vision.p2': 'We believe in intellectual formation that transcends rigid school structures, age limits, and national borders.',
    'vision.why.badge': 'Why Now',
    'vision.why.title': 'The world demands a new kind of learning.',
    'vision.why.p1': 'Traditional school structures were designed for a different era. They assume fixed schedules, local classrooms, and a linear path from enrollment to graduation. But students today live between countries. Adults seek knowledge beyond their degrees. Educators need spaces to rethink their practice.',
    'vision.why.p2': 'The rise of artificial intelligence is accelerating this shift. AI doesn\'t replace the need for deep thinking — it amplifies it. Students who understand both technology and humanities will lead the next generation. Educators who embrace AI as a tool, not a threat, will transform their classrooms.',
    'vision.why.p3': 'OFA Academy™ exists at this intersection: where rigorous intellectual tradition meets the demands of a connected, AI-augmented world. We offer programs that are short enough to be accessible, deep enough to be meaningful, and flexible enough to fit any life path.',
    'vision.beliefs.badge': 'Core Beliefs',
    'vision.beliefs.title': 'The principles that guide us.',
    'vision.beliefs.international': 'International by Design',
    'vision.beliefs.international.desc': 'Education should cross borders. Our programs serve learners from every continent, in multiple languages, with global perspectives embedded in every course.',
    'vision.beliefs.ai': 'AI as a Partner',
    'vision.beliefs.ai.desc': 'Artificial intelligence is transforming how we learn and work. We teach students and educators to harness AI while deepening the human skills it cannot replace.',
    'vision.beliefs.rigor': 'Intellectual Rigor',
    'vision.beliefs.rigor.desc': 'We don\'t simplify for convenience. Every program demands engagement, critical thinking, and genuine intellectual effort from its participants.',
    'vision.beliefs.lifelong': 'Lifelong Learning',
    'vision.beliefs.lifelong.desc': 'Learning doesn\'t end at graduation. Our Academy serves 14-year-olds and 60-year-olds alike, because curiosity has no expiration date.',
    'vision.beliefs.bilingual': 'Bilingual Excellence',
    'vision.beliefs.bilingual.desc': 'French and English are more than languages — they\'re gateways to different intellectual traditions. We cultivate excellence in both.',
    'vision.beliefs.formation': 'Formation, Not Just Information',
    'vision.beliefs.formation.desc': 'We don\'t just deliver content. We form thinkers — people who can analyze, question, create, and lead in an uncertain world.',
    'vision.connection.badge': 'Part of a Larger Vision',
    'vision.connection.title': 'Connected to OFALycée. Independent in mission.',
    'vision.connection.p1': 'OFA Academy™ is a sister institution to OFALycée, the international French-American online school. While OFALycée provides structured K-12 education, the Academy extends this vision to the broader world — offering flexible, non-traditional pathways for anyone seeking intellectual growth.',
    'vision.connection.p2': 'Together, they form a comprehensive educational ecosystem: one for structured schooling, one for lifelong learning. Both share the same commitment to bilingual excellence, global perspectives, and preparing minds for the future.',

    // Programs
    'programs.badge': 'Programs & Courses',
    'programs.title': 'Explore our catalog.',
    'programs.subtitle': 'For every age and every stage of life, OFA Academy™ brings together programs designed to spark critical thinking, nurture creativity, and prepare every learner for a changing world. An academy open to children, teens, students, teachers, parents, and curious lifelong learners — a place where learning has no age and no borders.',
    'programs.search': 'Search courses, instructors...',
    'programs.filters': 'Filters',
    'programs.searchBtn': 'Search',
    'programs.noResults': 'No programs found',
    'programs.noResults.desc': 'Our catalog is being curated. New programs will appear here as they are published. Check back soon or adjust your filters.',
    'programs.view': 'View',
    'programs.free': 'Free',
    'programs.comingSoon': 'Coming Soon',
    'programs.september2026': 'September 2026',
    'programs.cat.all': 'All Categories',
    'programs.cat.humanities': 'Humanities',
    'programs.cat.languages': 'Languages',
    'programs.cat.ai': 'AI & Future Skills',
    'programs.cat.pd': 'Professional Development',
    'programs.cat.certifications': 'Certifications',
    'programs.cat.seminars': 'Short Seminars',
    'programs.cat.summer': 'Summer / Intensive',
    'programs.aud.all': 'All Audiences',
    'programs.aud.students': 'Students',
    'programs.aud.elementary': 'Elementary',
    'programs.aud.middle-school': 'Middle School',
    'programs.aud.high-school': 'High School',
    'programs.aud.adults': 'Adults',
    'programs.aud.educators': 'Educators',
    'programs.fmt.all': 'All Formats',
    'programs.fmt.live': 'Live (online classes)',
    'programs.fmt.hybrid': 'Mixed (live + self-paced)',
    'programs.fmt.ondemand': 'Self-paced (on demand)',

    // Course Detail
    'course.back': 'Back to Programs',
    'course.about': 'About This Program',
    'course.outcomes': 'Learning Outcomes',
    'course.preview': 'Course Preview',
    'course.preview.desc': 'Get a glimpse of what you\'ll learn',
    'course.watchPreview': 'Watch Preview',
    'course.enroll': 'Enroll Now',
    'course.enrolled': 'Already Enrolled',
    'course.paymentNote': 'Payment integration coming soon',
    'course.notFound': 'Course not found',
    'course.comingSoonDesc': 'This course is currently being developed. Check back soon for enrollment details.',
    'course.duration': 'Duration',
    'course.audience': 'Audience',
    'course.format': 'Format',
    'course.category': 'Category',
    'course.instructor': 'Instructor',
    'course.modal.title': 'Choose Your Format',
    'course.modal.subtitle': 'Select the learning format that best fits your schedule and preferences.',
    'course.modal.ondemand': 'On Demand',
    'course.modal.ondemand.desc': 'Learn at your own pace with full access to course materials, recorded lectures, and assignments.',
    'course.modal.ondemand.feature1': 'Self-paced learning',
    'course.modal.ondemand.feature2': 'Full course materials access',
    'course.modal.ondemand.feature3': 'Recorded lectures',
    'course.modal.hybrid': 'Hybrid',
    'course.modal.hybrid.desc': 'Combine self-paced learning with live sessions and direct interaction with instructors.',
    'course.modal.hybrid.feature1': 'Live weekly sessions',
    'course.modal.hybrid.feature2': 'Direct instructor interaction',
    'course.modal.hybrid.feature3': 'Community & peer learning',
    'course.modal.hybrid.desc.parcours': 'All course materials plus a weekly 45-minute office hour session with a certified teacher for personalized support.',
    'course.modal.ondemand.4week.desc': 'Learn at your own pace over 4 weeks. Full access to all course materials, recorded content, and assignments.',
    'course.modal.hybrid.4week.desc': '4 weeks with 2 live office hour sessions for personalized feedback and interaction with your instructor.',
    'course.modal.hybrid.4week.feature1': '2 office hours with instructor',
    'course.modal.hybrid.feature1.parcours': 'Weekly 45min office hour with teacher',
    'course.modal.ondemand.histoire': 'Open Access',
    'course.modal.ondemand.histoire.desc': 'Study at your own pace with no fixed schedule. Complete the course in less than 8 weeks or take longer — you decide. Part 1 of "A History of France" includes 4 independent courses.',
    'course.modal.ondemand.histoire.feature1': 'Completely self-paced',
    'course.modal.ondemand.histoire.feature2': 'Can be completed in under 8 weeks',
    'course.modal.ondemand.histoire.feature3': 'Full course materials & assignments',
    'course.modal.guided': 'With Instructor',
    'course.modal.guided.desc': 'Follow a structured cohort schedule with live 45-minute sessions with your teacher on weeks 2, 4, 6 & 8. Cohorts run every 9 weeks (8 weeks of course + 1 week break). Next cohort starts June 29.',
    'course.modal.guided.feature1': '45min live session weeks 2, 4, 6 & 8',
    'course.modal.guided.feature2': 'Structured cohort pace (9-week cycles)',
    'course.modal.guided.feature3': 'Next cohort: June 29, 2026',
    'course.modal.recommended': 'Recommended',
    'course.modal.select': 'Select',
    'course.modal.confirm': 'Confirm Enrollment',
    'course.modal.confirming': 'Enrolling...',
    'course.modal.redirecting': 'Redirecting to payment...',

    // Payment
    'payment.processing': 'Processing your payment...',
    'payment.processing.desc': 'Please wait while we confirm your transaction.',
    'payment.success': 'Payment confirmed!',
    'payment.success.desc': 'You are now enrolled. A confirmation email has been sent to your inbox.',
    'payment.goDashboard': 'Go to My Dashboard',
    'payment.browseMore': 'Browse more courses',
    'payment.failed': 'Payment issue',
    'payment.failed.desc': 'Something went wrong with your payment. Please try again or contact us.',
    'payment.tryAgain': 'Back to Programs',

    // Certificates
    'certificates.badge': 'Certifications',
    'certificates.title': 'Recognized credentials for lifelong learners.',
    'certificates.subtitle': 'Earn certificates that validate your skills in philosophy, languages, AI literacy, and global thinking.',
    'certificates.loading': 'Loading certificates...',
    'certificates.filter.all': 'All Certificates',
    'certificates.noCerts': 'No certificates yet',
    'certificates.noCerts.desc': 'New certification programs will be available soon.',
    'certificates.outcomes': 'What you\'ll achieve',
    'certificates.apply': 'Apply Now',
    'certificates.learnMore': 'Learn More',
    'certificates.viewPath': 'View Path',
    'certificates.included': 'Included with courses',
    'certificates.cta.title': 'Ready to grow?',
    'certificates.cta.subtitle': 'Explore our programs and find the perfect learning path for your goals.',
    'certificates.cta.button': 'Browse Programs',

    // Team
    'team.badge': 'Our Team',
    'team.title': 'Meet the minds behind OFA Academy™.',
    'team.subtitle': 'Educators, thinkers, and innovators committed to transforming learning for a global world.',
    'team.loading': 'Loading team...',
    'team.noMembers': 'Team page coming soon',
    'team.noMembers.desc': 'We are building our team of exceptional educators.',
    'team.cta.title': 'Join our team',
    'team.cta.subtitle': 'We are always looking for passionate educators who share our vision of bilingual, thoughtful, future-ready education.',
    'team.cta.button': 'Apply to Teach',

    // Perspectives / Articles
    'perspectives.badge': 'Ideas & Voices',
    'perspectives.title': 'Ideas that shape education.',
    'perspectives.subtitle': 'Essays, analyses, and conversations from the OFA Academy™ team — and from voices beyond. We welcome contributions from educators, researchers, students, and anyone who wants to share thoughtful ideas about learning today.',
    'perspectives.loading': 'Loading articles...',
    'perspectives.filter.all': 'All Topics',
    'perspectives.cat.bilingual-learning': 'Bilingual Learning',
    'perspectives.cat.education-ai': 'Education & AI',
    'perspectives.cat.global-citizenship': 'Global Citizenship',
    'perspectives.cat.philosophy': 'Philosophy',
    'perspectives.cat.teaching-methods': 'Teaching Methods',
    'perspectives.cat.parent-resources': 'Parent Resources',
    'perspectives.cat.student-success': 'Student Success',
    'perspectives.search': 'Search articles...',
    'perspectives.noArticles': 'No articles yet',
    'perspectives.noArticles.desc': 'New perspectives will be published soon. Check back later.',
    'perspectives.read': 'Read',
    'perspectives.cta.title': 'Have a perspective to share?',
    'perspectives.cta.subtitle': 'We welcome contributions from educators, researchers, and thought leaders working at the intersection of education, technology, and global citizenship.',
    'perspectives.cta.button': 'Contribute an Article',

    // Article Page
    'article.loading': 'Loading article...',
    'article.notFound': 'Article not found',
    'article.backToList': 'Back to Perspectives',
    'article.share': 'Share',
    'article.linkCopied': 'Link copied to clipboard',
    'article.writtenBy': 'OFA Academy™',
    'article.related': 'Related Articles',

    // Certificates
    'certs.badge': 'Certificates & Learning Paths',
    'certs.title': 'Stackable learning for lasting impact.',
    'certs.subtitle': 'Our learning paths combine short courses into meaningful certificates. Each path is designed to build competency progressively — from foundations to advanced mastery.',
    'certs.stackable': 'Stackable model:',
    'certs.stackable.desc': 'Short courses lead to certificates, certificates lead to advanced seminars. Build at your own pace.',
    'certs.how.badge': 'How It Works',
    'certs.how.title': 'Your path, your pace.',
    'certs.how.step1': 'Choose a Course',
    'certs.how.step1.desc': 'Browse our catalog and enroll in individual courses that match your interests and goals.',
    'certs.how.step2': 'Earn Credits',
    'certs.how.step2.desc': 'Complete courses to earn credits toward a certificate in your chosen learning path.',
    'certs.how.step3': 'Achieve Certification',
    'certs.how.step3.desc': 'Complete all required courses in a path to receive your OFA Academy™ certificate.',
    'certs.paths.badge': 'Learning Paths',
    'certs.paths.title': 'Structured paths for serious learners.',
    'certs.paths.viewCourses': 'View Courses',
    'certs.path.ai': 'AI & Education Certificate',
    'certs.path.ai.desc': 'Master the intersection of artificial intelligence and pedagogy. Learn to implement AI tools in educational settings while understanding their ethical implications.',
    'certs.path.bilingual': 'Bilingual Academic Excellence',
    'certs.path.bilingual.desc': 'Develop advanced bilingual competency in French and English through literature, writing, and academic discourse.',
    'certs.path.global': 'Global Citizenship & Critical Thinking',
    'certs.path.global.desc': 'Explore international perspectives, cultural analysis, and the skills needed to engage meaningfully with a complex world.',
    'certs.path.french': 'French for International Pathways',
    'certs.path.french.desc': 'Build French language proficiency aligned with international academic and professional standards.',
    'certs.path.teacher': 'Teacher Development Series',
    'certs.path.teacher.desc': 'A comprehensive program for educators seeking to modernize their practice with research-backed methodologies and digital tools.',

    // Educators
    'edu.badge': 'Our Team & Educators',
    'edu.title': 'Meet the minds behind OFA Academy™.',
    'edu.subtitle': 'Educators, thinkers, and innovators committed to transforming learning for a global world. OFA Academy™ also offers dedicated programs for teachers and institutions.',
    'edu.apply': 'Join Our Team',
    'edu.cta': 'Request Partnership',
    'edu.offers.badge': 'What We Offer',
    'edu.offers.title': 'Programs built for educational professionals.',
    'edu.offer.badge': 'For Educators',
    'edu.offer.title': 'Programs designed for educational professionals.',
    'edu.offer.dev': 'Professional Development',
    'edu.offer.dev.desc': 'Research-driven professional development for educators at all levels. From classroom innovation to curriculum design.',
    'edu.offer.ai': 'AI in Education Workshops',
    'edu.offer.ai.desc': 'Hands-on workshops for educators exploring how to integrate AI tools responsibly into teaching and learning.',
    'edu.offer.collab': 'International Collaboration',
    'edu.offer.collab.desc': 'Connect with educators worldwide through cross-cultural projects, exchanges, and collaborative learning initiatives.',
    'edu.offer.partner': 'School Partnerships',
    'edu.offer.partner.desc': 'Partner with OFA Academy™ to bring global, bilingual, and AI-integrated programs to your institution.',
    'edu.offer.workshops': 'Custom Workshops',
    'edu.offer.workshops.desc': 'Tailored learning solutions for schools and organizations with specific professional development needs.',
    'edu.offer.training': 'Teacher Training Programs',
    'edu.offer.training.desc': 'Research-driven professional development for educators at all levels. From classroom innovation to curriculum design.',
    'edu.offer.partnerships': 'School Partnerships',
    'edu.offer.partnerships.desc': 'Partner with OFA Academy™ to bring global, bilingual, and AI-integrated programs to your institution.',
    'edu.offer.custom': 'Custom Programs',
    'edu.offer.custom.desc': 'Tailored learning solutions for schools and organizations with specific professional development needs.',
    'edu.offer.intl': 'International Collaboration',
    'edu.offer.intl.desc': 'Connect with educators worldwide through cross-cultural projects, exchanges, and collaborative learning initiatives.',
    'edu.offer.leadership': 'Leadership Development',
    'edu.offer.leadership.desc': 'Programs designed for school leaders and administrators seeking to guide their institutions into the future of education.',
    'edu.bottom.title': 'Ready to transform your institution?',
    'edu.bottom.subtitle': 'Whether you\'re an individual educator or a school leader, we\'d love to explore how we can work together.',
    'edu.bottom.cta': 'Get in Touch',

    // Contribute
    'contrib.badge': 'Teach or Contribute',
    'contrib.title': 'Share your expertise with the world.',
    'contrib.subtitle': 'We\'re looking for international teachers, researchers, experts, and thought leaders to join our growing community of instructors.',
    'contrib.form.title': 'Submit Your Proposal',
    'contrib.form.name': 'Full Name',
    'contrib.form.name.ph': 'Your full name',
    'contrib.form.email': 'Email',
    'contrib.form.email.ph': 'your@email.com',
    'contrib.form.field': 'Field of Expertise',
    'contrib.form.field.ph': 'Select your field',
    'contrib.form.proposal': 'Course Proposal',
    'contrib.form.proposal.ph': 'Describe the course or workshop you\'d like to offer. Include target audience, format preference, and learning objectives.',
    'contrib.form.cv': 'CV / Resume (PDF, DOC, DOCX)',
    'contrib.form.cv.choose': 'Choose file',
    'contrib.form.cv.none': 'No file chosen',
    'contrib.form.submit': 'Submit Proposal',
    'contrib.form.submitting': 'Submitting...',
    'contrib.sidebar.title': 'Who We\'re Looking For',
    'contrib.sidebar.1': 'International teachers with cross-cultural experience',
    'contrib.sidebar.2': 'Researchers and thought leaders in education',
    'contrib.sidebar.3': 'Experts in AI, humanities, or languages',
    'contrib.success.title': 'Thank you for your interest.',
    'contrib.success.desc': 'We\'ve received your proposal and will review it carefully. Expect to hear from our team within two weeks.',
    'contrib.success.again': 'Submit another application',

    // Login / Register
    'login.title': 'Welcome back',
    'login.subtitle': 'Sign in to your academy account',
    'login.welcomeBack': 'Welcome back',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.submit': 'Sign In',
    'login.signing': 'Signing in...',
    'login.noAccount': 'Don\'t have an account?',
    'login.createOne': 'Create one',
    'login.forgotPassword': 'Forgot your password?',
    'register.title': 'Create your account',
    'register.subtitle': 'Join the OFA Academy™ community',
    'register.name': 'Full Name',
    'register.email': 'Email',
    'register.password': 'Password',
    'register.submit': 'Create Account',
    'register.creating': 'Creating account...',
    'register.hasAccount': 'Already have an account?',
    'register.signin': 'Sign in',

    // Student Dashboard
    'dashboard.title': 'My Learning Dashboard',
    'dashboard.subtitle': 'Track your enrolled courses and learning progress.',
    'dashboard.enrolled': 'Enrolled Courses',
    'dashboard.noCourses': 'No enrollments yet',
    'dashboard.noCourses.desc': 'Browse our catalog and enroll in a course to start your learning journey.',
    'dashboard.explore': 'Explore Programs',
    'dashboard.enrolledOn': 'Enrolled on',
    'dashboard.viewCourse': 'View Course',
    'dashboard.accessCanvas': 'Access Course on Canvas',
    'dashboard.status.active': 'Active',
    'dashboard.status.completed': 'Completed',
    'dashboard.progress': 'Progress',
    'dashboard.progress.markComplete': 'Mark as complete',
    'dashboard.progress.completed': 'Course completed',
    'dashboard.progress.completedOn': 'Completed on',
    'dashboard.progress.updated': 'Progress updated',
    'dashboard.progress.completedToast': 'Congratulations — course completed! 🎉',
    'dashboard.progress.error': 'Could not update progress',
    'dashboard.certificate.download': 'Download certificate',
    'dashboard.resendEmail': 'Resend confirmation email',
    'dashboard.resendEmail.sending': 'Sending...',
    'dashboard.resendEmail.success': 'Confirmation email sent to your inbox.',
    'dashboard.resendEmail.error': 'Could not resend email. Please try again later.',

    // Footer
    'footer.desc': 'A global learning space for students, educators, and adults who want to think, learn, and grow beyond traditional schooling.',
    'footer.global': 'Global Institute',
    'footer.academy': 'Academy',
    'footer.community': 'Community',
    'footer.contact': 'Contact',
    'footer.teachContribute': 'Teach or Contribute',
    'footer.school': 'OFALycée School',
    'footer.blog': 'OFA Blog',
    'perspectives.blog.title': 'Continue Exploring on Our Blog',
    'perspectives.blog.subtitle': 'For long-form essays, alumni stories and pedagogical reflections, visit our companion blog — part of the same OFA Lycée™ ecosystem.',
    'perspectives.blog.button': 'Visit OFA Blog',
    'footer.studentPortal': 'Student Portal',
    'footer.bilingual': 'Bilingual EN / FR',
    'footer.globalOnline': 'Global — Online',
    'footer.rights': 'All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
  },

  fr: {
    // Navbar
    'nav.home': 'Accueil',
    'nav.vision': 'Vision',
    'nav.programs': 'Programmes',
    'nav.summer': "École d'été",
    'nav.perspectives': 'Perspectives',
    'nav.certificates': 'Certificats',
    'nav.educators': 'Notre équipe',
    'nav.contribute': 'Contribuer',
    'nav.signin': 'Connexion',
    'nav.dashboard': 'Mon Espace',
    'nav.admin': 'Administration',
    'nav.signout': 'Déconnexion',

    // Landing
    'landing.welcome.title': 'Bon retour',
    'landing.welcome.subtitle': 'Heureux de vous revoir sur OFA Academy™.',
    'landing.badge': 'OFA Academy™',
    'landing.online.pill': '100% en ligne · Accessible partout dans le monde',
    'landing.hero.title': 'Une académie mondiale pour un monde en mutation.',
    'landing.hero.subtitle': 'Une académie entièrement en ligne — apprendre au-delà des frontières, des disciplines et des étapes de la vie. Un institut mondial indépendant pour étudiants, enseignants et apprenants tout au long de la vie.',
    'landing.hero.cta': 'Explorer les programmes',
    'landing.hero.vision': 'Notre vision',
    'landing.about.badge': 'À propos de l\'Académie',
    'landing.about.title': 'Plus qu\'une école. Un espace intellectuel mondial.',
    'landing.about.p1': 'OFA Academy™ est une nouvelle structure d\'apprentissage liée à OFALycée — l\'école internationale franco-américaine en ligne — mais conçue comme une académie mondiale indépendante.',
    'landing.about.p2': 'Ce n\'est pas une école traditionnelle et ne repose pas sur une inscription annuelle. Elle offre des expériences d\'apprentissage flexibles et de haut niveau combinant humanités, langues, IA et éducation internationale pour un public mondial.',
    'landing.about.link': 'Lire notre philosophie',
    'landing.about.year': 'Fondée en 2026',
    'landing.about.label': 'Institut mondial',
    'landing.video.badge': 'À découvrir',
    'landing.video.title': 'L\'avenir de l\'apprentissage est là.',
    'landing.video.subtitle': 'Découvrez comment la technologie et la connexion humaine transforment l\'éducation pour la prochaine génération.',
    'landing.video.quote': 'L\'IA représente probablement la plus grande transformation positive que l\'éducation ait jamais connue.',
    'landing.video.description': 'À OFA Academy™, nous adoptons l\'IA comme partenaire d\'apprentissage — amplifiant le potentiel humain tout en préservant la réflexion profonde et la richesse culturelle qui définissent la véritable éducation.',
    'landing.video.learnMore': 'Explorer notre vision',
    'landing.audience.badge': 'Pour qui',
    'landing.audience.title': 'Conçu pour les esprits curieux à chaque étape.',
    'landing.audience.students': 'Étudiants',
    'landing.audience.students.desc': 'Du collège à la préparation universitaire, des apprenants en quête d\'excellence académique mondiale.',
    'landing.audience.educators': 'Enseignants',
    'landing.audience.educators.desc': 'Enseignants et responsables scolaires qui font évoluer leur pratique grâce à des programmes fondés sur la recherche.',
    'landing.audience.adults': 'Adultes',
    'landing.audience.adults.desc': 'Apprenants tout au long de la vie explorant les humanités, l\'IA et les perspectives internationales.',
    'landing.audience.families': 'Familles internationales',
    'landing.audience.families.desc': 'Familles naviguant dans l\'éducation bilingue et les parcours académiques interculturels.',
    'landing.pillars.badge': 'Piliers d\'apprentissage',
    'landing.pillars.title': 'Là où la tradition rencontre l\'avenir.',
    'landing.pillars.humanities': 'Humanités',
    'landing.pillars.humanities.desc': 'Pensée critique, philosophie, littérature et études culturelles.',
    'landing.pillars.languages': 'Langues',
    'landing.pillars.languages.desc': 'Excellence bilingue en français et en anglais pour la communication mondiale.',
    'landing.pillars.ai': 'IA & Compétences du futur',
    'landing.pillars.ai.desc': 'Comprendre l\'intelligence artificielle et son rôle dans la transformation de l\'éducation.',
    'landing.pillars.formation': 'Formation intellectuelle',
    'landing.pillars.formation.desc': 'Un apprentissage profond qui favorise l\'indépendance, la créativité et la rigueur.',
    'landing.cta.title': 'Commencez votre parcours d\'apprentissage.',
    'landing.cta.subtitle': 'Explorez notre catalogue de programmes conçus pour un public mondial. Formats flexibles, enseignement premium, résultats significatifs.',
    'landing.cta.explore': 'Explorer les programmes',
    'landing.cta.teach': 'Enseigner avec nous',

    // Vision
    'vision.badge': 'Vision & Philosophie',
    'vision.title': 'L\'éducation doit évoluer.',
    'vision.subtitle': 'Le monde a changé. Notre façon d\'apprendre doit changer avec lui. OFA Academy™ est née d\'une conviction : l\'éducation peut être rigoureuse, flexible et mondialement significative — tout à la fois.',
    'vision.p2': 'Nous croyons en une formation intellectuelle qui transcende les structures scolaires rigides, les limites d\'âge et les frontières nationales.',
    'vision.why.badge': 'Pourquoi maintenant',
    'vision.why.title': 'Le monde exige un nouveau type d\'apprentissage.',
    'vision.why.p1': 'Les structures scolaires traditionnelles ont été conçues pour une autre époque. Elles supposent des horaires fixes, des salles de classe locales et un parcours linéaire de l\'inscription à l\'obtention du diplôme. Mais les étudiants d\'aujourd\'hui vivent entre les pays. Les adultes cherchent des connaissances au-delà de leurs diplômes. Les enseignants ont besoin d\'espaces pour repenser leur pratique.',
    'vision.why.p2': 'L\'essor de l\'intelligence artificielle accélère ce changement. L\'IA ne remplace pas le besoin de réflexion profonde — elle l\'amplifie. Les étudiants qui comprennent à la fois la technologie et les humanités dirigeront la prochaine génération. Les enseignants qui adoptent l\'IA comme un outil, et non une menace, transformeront leurs classes.',
    'vision.why.p3': 'OFA Academy™ existe à cette intersection : là où la tradition intellectuelle rigoureuse rencontre les exigences d\'un monde connecté et augmenté par l\'IA. Nous proposons des programmes assez courts pour être accessibles, assez profonds pour être significatifs et assez flexibles pour s\'adapter à tout parcours de vie.',
    'vision.beliefs.badge': 'Convictions fondamentales',
    'vision.beliefs.title': 'Les principes qui nous guident.',
    'vision.beliefs.international': 'International par nature',
    'vision.beliefs.international.desc': 'L\'éducation devrait traverser les frontières. Nos programmes servent des apprenants de tous les continents, en plusieurs langues, avec des perspectives mondiales intégrées dans chaque cours.',
    'vision.beliefs.ai': 'L\'IA comme partenaire',
    'vision.beliefs.ai.desc': 'L\'intelligence artificielle transforme notre façon d\'apprendre et de travailler. Nous enseignons aux étudiants et aux enseignants à exploiter l\'IA tout en approfondissant les compétences humaines qu\'elle ne peut remplacer.',
    'vision.beliefs.rigor': 'Rigueur intellectuelle',
    'vision.beliefs.rigor.desc': 'Nous ne simplifions pas pour la commodité. Chaque programme exige engagement, pensée critique et effort intellectuel authentique de ses participants.',
    'vision.beliefs.lifelong': 'Apprentissage tout au long de la vie',
    'vision.beliefs.lifelong.desc': 'L\'apprentissage ne s\'arrête pas à l\'obtention du diplôme. Notre Académie accueille aussi bien les jeunes de 14 ans que les adultes de 60 ans, car la curiosité n\'a pas de date d\'expiration.',
    'vision.beliefs.bilingual': 'Excellence bilingue',
    'vision.beliefs.bilingual.desc': 'Le français et l\'anglais sont plus que des langues — ce sont des portes d\'entrée vers des traditions intellectuelles différentes. Nous cultivons l\'excellence dans les deux.',
    'vision.beliefs.formation': 'Formation, pas seulement information',
    'vision.beliefs.formation.desc': 'Nous ne livrons pas simplement du contenu. Nous formons des penseurs — des personnes capables d\'analyser, de questionner, de créer et de diriger dans un monde incertain.',
    'vision.connection.badge': 'Partie d\'une vision plus large',
    'vision.connection.title': 'Connecté à OFALycée. Indépendant dans sa mission.',
    'vision.connection.p1': 'OFA Academy™ est une institution sœur d\'OFALycée, l\'école internationale franco-américaine en ligne. Tandis qu\'OFALycée offre un enseignement structuré de la maternelle au lycée, l\'Académie étend cette vision au monde entier — offrant des parcours flexibles et non traditionnels pour quiconque recherche la croissance intellectuelle.',
    'vision.connection.p2': 'Ensemble, ils forment un écosystème éducatif complet : l\'un pour l\'enseignement structuré, l\'autre pour l\'apprentissage tout au long de la vie. Les deux partagent le même engagement envers l\'excellence bilingue, les perspectives mondiales et la préparation des esprits pour l\'avenir.',

    // Programs
    'programs.badge': 'Programmes & Cours',
    'programs.title': 'Explorez notre catalogue.',
    'programs.subtitle': 'Pour tous les âges et toutes les étapes de la vie, OFA Academy™ rassemble des programmes pensés pour éveiller l\'esprit critique, nourrir la créativité et préparer chacun à un monde en mutation. Une académie ouverte aux enfants, adolescents, étudiants, enseignants, parents et apprenants curieux — un lieu où l\'apprentissage n\'a ni âge ni frontières.',
    'programs.search': 'Rechercher des cours, instructeurs...',
    'programs.filters': 'Filtres',
    'programs.searchBtn': 'Rechercher',
    'programs.noResults': 'Aucun programme trouvé',
    'programs.noResults.desc': 'Notre catalogue est en cours de curation. De nouveaux programmes apparaîtront ici dès leur publication.',
    'programs.view': 'Voir',
    'programs.free': 'Gratuit',
    'programs.comingSoon': 'Bientôt disponible',
    'programs.september2026': 'Septembre 2026',
    'programs.cat.all': 'Toutes les catégories',
    'programs.cat.humanities': 'Humanités',
    'programs.cat.languages': 'Langues',
    'programs.cat.ai': 'IA & Compétences du futur',
    'programs.cat.pd': 'Développement professionnel',
    'programs.cat.certifications': 'Certifications',
    'programs.cat.seminars': 'Séminaires courts',
    'programs.cat.summer': 'Été / Intensif',
    'programs.aud.all': 'Tous les publics',
    'programs.aud.students': 'Étudiants',
    'programs.aud.elementary': 'Élèves primaire',
    'programs.aud.middle-school': 'Collégiens',
    'programs.aud.high-school': 'Lycéens',
    'programs.aud.adults': 'Adultes',
    'programs.aud.educators': 'Enseignants',
    'programs.fmt.all': 'Tous les formats',
    'programs.fmt.live': 'En direct (cours en visio)',
    'programs.fmt.hybrid': 'Mixte (visio + à votre rythme)',
    'programs.fmt.ondemand': 'À votre rythme (à la demande)',

    // Course Detail
    'course.back': 'Retour aux programmes',
    'course.about': 'À propos de ce programme',
    'course.outcomes': 'Résultats d\'apprentissage',
    'course.preview': 'Aperçu du cours',
    'course.preview.desc': 'Découvrez ce que vous allez apprendre',
    'course.watchPreview': 'Voir l\'aperçu',
    'course.enroll': 'S\'inscrire',
    'course.enrolled': 'Déjà inscrit',
    'course.paymentNote': 'Intégration de paiement à venir',
    'course.notFound': 'Cours non trouvé',
    'course.comingSoonDesc': 'Ce cours est en cours de développement. Revenez bientôt pour les détails d\'inscription.',
    'course.duration': 'Durée',
    'course.audience': 'Public',
    'course.format': 'Format',
    'course.category': 'Catégorie',
    'course.instructor': 'Instructeur',
    'course.modal.title': 'Choisissez votre format',
    'course.modal.subtitle': 'Sélectionnez le format d\'apprentissage qui correspond le mieux à votre emploi du temps.',
    'course.modal.ondemand': 'À la demande',
    'course.modal.ondemand.desc': 'Apprenez à votre rythme avec un accès complet aux supports de cours, vidéos et devoirs.',
    'course.modal.ondemand.feature1': 'Apprentissage à votre rythme',
    'course.modal.ondemand.feature2': 'Accès complet aux supports',
    'course.modal.ondemand.feature3': 'Cours enregistrés',
    'course.modal.hybrid': 'Hybride',
    'course.modal.hybrid.desc': 'Combinez l\'apprentissage autonome avec des sessions en direct et l\'interaction avec les enseignants.',
    'course.modal.hybrid.feature1': 'Sessions hebdomadaires en direct',
    'course.modal.hybrid.feature2': 'Interaction directe avec l\'enseignant',
    'course.modal.hybrid.feature3': 'Communauté & apprentissage entre pairs',
    'course.modal.hybrid.desc.parcours': 'Tous les supports de cours plus une session hebdomadaire de 45 minutes d\'office hour avec un enseignant certifié pour un accompagnement personnalisé.',
    'course.modal.ondemand.4week.desc': 'Apprenez à votre rythme sur 4 semaines. Accès complet aux supports de cours, contenus enregistrés et devoirs.',
    'course.modal.hybrid.4week.desc': '4 semaines avec 2 sessions d\'office hour en direct pour un feedback personnalisé et une interaction avec votre enseignant.',
    'course.modal.hybrid.4week.feature1': '2 office hours avec l\'enseignant',
    'course.modal.hybrid.feature1.parcours': '45min d\'office hour hebdomadaire avec un enseignant',
    'course.modal.ondemand.histoire': 'Accès libre',
    'course.modal.ondemand.histoire.desc': 'Étudiez à votre rythme sans calendrier imposé. Terminez le cours en moins de 8 semaines ou prenez plus de temps — c\'est vous qui décidez. La Partie 1 de « Une Histoire de France » comprend 4 cours indépendants.',
    'course.modal.ondemand.histoire.feature1': 'Entièrement à votre rythme',
    'course.modal.ondemand.histoire.feature2': 'Peut être terminé en moins de 8 semaines',
    'course.modal.ondemand.histoire.feature3': 'Supports de cours & devoirs inclus',
    'course.modal.guided': 'Suivi professeur',
    'course.modal.guided.desc': 'Suivez un calendrier de cohorte structuré avec des sessions de 45 minutes en direct avec votre enseignant aux semaines 2, 4, 6 et 8. Les cohortes se succèdent toutes les 9 semaines (8 semaines de cours + 1 semaine de pause). Prochaine cohorte : 29 juin.',
    'course.modal.guided.feature1': 'Session de 45min semaines 2, 4, 6 et 8',
    'course.modal.guided.feature2': 'Rythme de cohorte structuré (cycles de 9 semaines)',
    'course.modal.guided.feature3': 'Prochaine cohorte : 29 juin 2026',
    'course.modal.recommended': 'Recommandé',
    'course.modal.select': 'Sélectionner',
    'course.modal.confirm': 'Confirmer l\'inscription',
    'course.modal.confirming': 'Inscription en cours...',
    'course.modal.redirecting': 'Redirection vers le paiement...',

    // Payment
    'payment.processing': 'Traitement du paiement...',
    'payment.processing.desc': 'Veuillez patienter pendant que nous confirmons votre transaction.',
    'payment.success': 'Paiement confirmé !',
    'payment.success.desc': 'Vous êtes maintenant inscrit. Un email de confirmation a été envoyé.',
    'payment.goDashboard': 'Aller à mon tableau de bord',
    'payment.browseMore': 'Explorer d\'autres cours',
    'payment.failed': 'Problème de paiement',
    'payment.failed.desc': 'Un problème est survenu avec votre paiement. Veuillez réessayer ou nous contacter.',
    'payment.tryAgain': 'Retour aux programmes',

    // Certificates
    'certificates.badge': 'Certifications',
    'certificates.title': 'Des certificats reconnus pour les apprenants à vie.',
    'certificates.subtitle': 'Obtenez des certificats qui valident vos compétences en philosophie, langues, littératie IA et pensée globale.',
    'certificates.loading': 'Chargement des certificats...',
    'certificates.filter.all': 'Tous les certificats',
    'certificates.noCerts': 'Aucun certificat pour le moment',
    'certificates.noCerts.desc': 'De nouveaux programmes de certification seront bientôt disponibles.',
    'certificates.outcomes': 'Ce que vous accomplirez',
    'certificates.apply': 'Postuler',
    'certificates.learnMore': 'En savoir plus',
    'certificates.viewPath': 'Voir le parcours',
    'certificates.included': 'Inclus avec les cours',
    'certificates.cta.title': 'Prêt à progresser ?',
    'certificates.cta.subtitle': 'Explorez nos programmes et trouvez le parcours d\'apprentissage idéal pour vos objectifs.',
    'certificates.cta.button': 'Parcourir les programmes',

    // Team
    'team.badge': 'Notre équipe',
    'team.title': 'Rencontrez les esprits derrière OFA Academy™.',
    'team.subtitle': 'Enseignants, penseurs et innovateurs engagés à transformer l\'apprentissage pour un monde global.',
    'team.loading': 'Chargement de l\'équipe...',
    'team.noMembers': 'Page équipe à venir',
    'team.noMembers.desc': 'Nous construisons notre équipe d\'enseignants exceptionnels.',
    'team.cta.title': 'Rejoignez notre équipe',
    'team.cta.subtitle': 'Nous recherchons toujours des enseignants passionnés qui partagent notre vision d\'une éducation bilingue, réfléchie et tournée vers l\'avenir.',
    'team.cta.button': 'Postuler pour enseigner',

    // Perspectives / Articles
    'perspectives.badge': 'Idées & Voix',
    'perspectives.title': 'Des idées qui façonnent l\'éducation.',
    'perspectives.subtitle': 'Essais, analyses et conversations de l\'équipe d\'OFA Academy™ — et de voix venues d\'ailleurs. Nous accueillons les contributions d\'enseignants, de chercheurs, d\'élèves et de toute personne souhaitant partager une réflexion sur l\'apprentissage aujourd\'hui.',
    'perspectives.loading': 'Chargement des articles...',
    'perspectives.filter.all': 'Tous les sujets',
    'perspectives.cat.bilingual-learning': 'Apprentissage bilingue',
    'perspectives.cat.education-ai': 'Éducation & IA',
    'perspectives.cat.global-citizenship': 'Citoyenneté mondiale',
    'perspectives.cat.philosophy': 'Philosophie',
    'perspectives.cat.teaching-methods': 'Méthodes pédagogiques',
    'perspectives.cat.parent-resources': 'Ressources parents',
    'perspectives.cat.student-success': 'Réussite étudiante',
    'perspectives.search': 'Rechercher des articles...',
    'perspectives.noArticles': 'Aucun article',
    'perspectives.noArticles.desc': 'De nouvelles perspectives seront publiées prochainement.',
    'perspectives.read': 'Lire',
    'perspectives.cta.title': 'Une perspective à partager ?',
    'perspectives.cta.subtitle': 'Nous accueillons les contributions d\'enseignants, chercheurs et leaders d\'opinion travaillant à l\'intersection de l\'éducation, de la technologie et de la citoyenneté mondiale.',
    'perspectives.cta.button': 'Contribuer un article',

    // Article Page
    'article.loading': 'Chargement de l\'article...',
    'article.notFound': 'Article non trouvé',
    'article.backToList': 'Retour aux Perspectives',
    'article.share': 'Partager',
    'article.linkCopied': 'Lien copié',
    'article.writtenBy': 'OFA Academy™',
    'article.related': 'Articles connexes',

    // Certificates
    'certs.badge': 'Certificats & Parcours',
    'certs.title': 'Un apprentissage cumulable pour un impact durable.',
    'certs.subtitle': 'Nos parcours combinent des cours courts en certificats significatifs. Chaque parcours est conçu pour développer les compétences progressivement.',
    'certs.stackable': 'Modèle cumulable :',
    'certs.stackable.desc': 'Les cours courts mènent aux certificats, les certificats mènent aux séminaires avancés. Construisez à votre rythme.',
    'certs.how.badge': 'Comment ça marche',
    'certs.how.title': 'Votre parcours, votre rythme.',
    'certs.how.step1': 'Choisir un cours',
    'certs.how.step1.desc': 'Parcourez notre catalogue et inscrivez-vous à des cours individuels selon vos intérêts et objectifs.',
    'certs.how.step2': 'Obtenir des crédits',
    'certs.how.step2.desc': 'Complétez des cours pour obtenir des crédits vers un certificat dans votre parcours choisi.',
    'certs.how.step3': 'Obtenir la certification',
    'certs.how.step3.desc': 'Complétez tous les cours requis dans un parcours pour recevoir votre certificat OFA Academy™.',
    'certs.paths.badge': 'Parcours d\'apprentissage',
    'certs.paths.title': 'Des parcours structurés pour les apprenants sérieux.',
    'certs.paths.viewCourses': 'Voir les cours',
    'certs.path.ai': 'Certificat IA & Éducation',
    'certs.path.ai.desc': 'Maîtrisez l\'intersection entre intelligence artificielle et pédagogie. Apprenez à implémenter les outils IA dans les contextes éducatifs.',
    'certs.path.bilingual': 'Excellence académique bilingue',
    'certs.path.bilingual.desc': 'Développez une compétence bilingue avancée en français et en anglais à travers la littérature, l\'écriture et le discours académique.',
    'certs.path.global': 'Citoyenneté mondiale & Pensée critique',
    'certs.path.global.desc': 'Explorez les perspectives internationales, l\'analyse culturelle et les compétences nécessaires pour s\'engager dans un monde complexe.',
    'certs.path.french': 'Français pour les parcours internationaux',
    'certs.path.french.desc': 'Développez la maîtrise du français alignée sur les standards académiques et professionnels internationaux.',
    'certs.path.teacher': 'Série développement enseignant',
    'certs.path.teacher.desc': 'Un programme complet pour les enseignants cherchant à moderniser leur pratique avec des méthodologies fondées sur la recherche.',

    // Educators / Team
    'edu.badge': 'Notre équipe & enseignants',
    'edu.title': 'Rencontrez les esprits derrière OFA Academy™.',
    'edu.subtitle': 'Enseignants, penseurs et innovateurs engagés à transformer l\'apprentissage pour un monde global. OFA Academy™ propose aussi des programmes dédiés aux enseignants et institutions.',
    'edu.apply': 'Rejoindre notre équipe',
    'edu.cta': 'Demander un partenariat',
    'edu.offers.badge': 'Ce que nous offrons',
    'edu.offers.title': 'Des programmes conçus pour les professionnels de l\'éducation.',
    'edu.offer.badge': 'Pour les enseignants',
    'edu.offer.title': 'Des programmes conçus pour les professionnels de l\'éducation.',
    'edu.offer.dev': 'Développement professionnel',
    'edu.offer.dev.desc': 'Développement professionnel fondé sur la recherche pour les enseignants de tous niveaux.',
    'edu.offer.ai': 'Ateliers IA en éducation',
    'edu.offer.ai.desc': 'Ateliers pratiques pour les enseignants explorant l\'intégration responsable des outils IA dans l\'enseignement.',
    'edu.offer.collab': 'Collaboration internationale',
    'edu.offer.collab.desc': 'Connectez-vous avec des enseignants du monde entier à travers des projets interculturels et des initiatives collaboratives.',
    'edu.offer.partner': 'Partenariats scolaires',
    'edu.offer.partner.desc': 'Associez-vous à OFA Academy™ pour apporter des programmes mondiaux, bilingues et intégrés à l\'IA dans votre institution.',
    'edu.offer.workshops': 'Ateliers sur mesure',
    'edu.offer.workshops.desc': 'Solutions d\'apprentissage adaptées aux besoins spécifiques de développement professionnel des écoles et organisations.',
    'edu.offer.training': 'Programmes de formation des enseignants',
    'edu.offer.training.desc': 'Développement professionnel fondé sur la recherche pour les enseignants de tous niveaux.',
    'edu.offer.partnerships': 'Partenariats scolaires',
    'edu.offer.partnerships.desc': 'Associez-vous à OFA Academy™ pour apporter des programmes mondiaux, bilingues et intégrés à l\'IA dans votre institution.',
    'edu.offer.custom': 'Programmes sur mesure',
    'edu.offer.custom.desc': 'Solutions d\'apprentissage adaptées aux besoins spécifiques de développement professionnel des écoles et organisations.',
    'edu.offer.intl': 'Collaboration internationale',
    'edu.offer.intl.desc': 'Connectez-vous avec des enseignants du monde entier à travers des projets interculturels et des initiatives collaboratives.',
    'edu.offer.leadership': 'Développement du leadership',
    'edu.offer.leadership.desc': 'Programmes conçus pour les responsables scolaires souhaitant guider leurs institutions vers l\'avenir de l\'éducation.',
    'edu.bottom.title': 'Prêt à transformer votre institution ?',
    'edu.bottom.subtitle': 'Que vous soyez enseignant ou responsable d\'établissement, nous serions ravis d\'explorer comment travailler ensemble.',
    'edu.bottom.cta': 'Nous contacter',

    // Contribute
    'contrib.badge': 'Enseigner ou contribuer',
    'contrib.title': 'Partagez votre expertise avec le monde.',
    'contrib.subtitle': 'Nous recherchons des enseignants internationaux, chercheurs, experts et leaders d\'opinion pour rejoindre notre communauté d\'instructeurs.',
    'contrib.form.title': 'Soumettre votre proposition',
    'contrib.form.name': 'Nom complet',
    'contrib.form.name.ph': 'Votre nom complet',
    'contrib.form.email': 'Email',
    'contrib.form.email.ph': 'votre@email.com',
    'contrib.form.field': 'Domaine d\'expertise',
    'contrib.form.field.ph': 'Sélectionnez votre domaine',
    'contrib.form.proposal': 'Proposition de cours',
    'contrib.form.proposal.ph': 'Décrivez le cours ou l\'atelier que vous souhaitez proposer. Incluez le public cible, le format préféré et les objectifs d\'apprentissage.',
    'contrib.form.cv': 'CV (PDF, DOC, DOCX)',
    'contrib.form.cv.choose': 'Choisir un fichier',
    'contrib.form.cv.none': 'Aucun fichier choisi',
    'contrib.form.submit': 'Soumettre la proposition',
    'contrib.form.submitting': 'Envoi en cours...',
    'contrib.sidebar.title': 'Qui nous recherchons',
    'contrib.sidebar.1': 'Enseignants internationaux avec une expérience interculturelle',
    'contrib.sidebar.2': 'Chercheurs et leaders d\'opinion en éducation',
    'contrib.sidebar.3': 'Experts en IA, humanités ou langues',
    'contrib.success.title': 'Merci pour votre intérêt.',
    'contrib.success.desc': 'Nous avons bien reçu votre proposition et l\'examinerons attentivement. Vous recevrez une réponse de notre équipe sous deux semaines.',
    'contrib.success.again': 'Soumettre une autre candidature',

    // Login / Register
    'login.title': 'Bon retour',
    'login.subtitle': 'Connectez-vous à votre compte',
    'login.welcomeBack': 'Bon retour',
    'login.email': 'Email',
    'login.password': 'Mot de passe',
    'login.submit': 'Se connecter',
    'login.signing': 'Connexion...',
    'login.noAccount': 'Pas encore de compte ?',
    'login.createOne': 'Créer un compte',
    'login.forgotPassword': 'Mot de passe oublié ?',
    'register.title': 'Créer votre compte',
    'register.subtitle': 'Rejoignez la communauté OFA Academy™',
    'register.name': 'Nom complet',
    'register.email': 'Email',
    'register.password': 'Mot de passe',
    'register.submit': 'Créer le compte',
    'register.creating': 'Création...',
    'register.hasAccount': 'Déjà un compte ?',
    'register.signin': 'Se connecter',

    // Student Dashboard
    'dashboard.title': 'Mon espace d\'apprentissage',
    'dashboard.subtitle': 'Suivez vos cours et votre progression.',
    'dashboard.enrolled': 'Cours inscrits',
    'dashboard.noCourses': 'Aucune inscription',
    'dashboard.noCourses.desc': 'Parcourez notre catalogue et inscrivez-vous à un cours pour commencer votre parcours.',
    'dashboard.explore': 'Explorer les programmes',
    'dashboard.enrolledOn': 'Inscrit le',
    'dashboard.viewCourse': 'Voir le cours',
    'dashboard.accessCanvas': 'Accéder au cours sur Canvas',
    'dashboard.status.active': 'Actif',
    'dashboard.status.completed': 'Terminé',
    'dashboard.progress': 'Progression',
    'dashboard.progress.markComplete': 'Marquer comme terminé',
    'dashboard.progress.completed': 'Cours terminé',
    'dashboard.progress.completedOn': 'Terminé le',
    'dashboard.progress.updated': 'Progression mise à jour',
    'dashboard.progress.completedToast': 'Bravo — cours terminé ! 🎉',
    'dashboard.progress.error': 'Impossible de mettre à jour la progression',
    'dashboard.certificate.download': 'Télécharger le certificat',
    'dashboard.resendEmail': 'Renvoyer l\'email de confirmation',
    'dashboard.resendEmail.sending': 'Envoi en cours...',
    'dashboard.resendEmail.success': 'Email de confirmation renvoyé dans votre boîte mail.',
    'dashboard.resendEmail.error': 'Impossible de renvoyer l\'email. Veuillez réessayer plus tard.',

    // Footer
    'footer.desc': 'Un espace d\'apprentissage mondial pour les étudiants, enseignants et adultes qui veulent penser, apprendre et grandir au-delà de l\'école traditionnelle.',
    'footer.global': 'Institut mondial',
    'footer.academy': 'Académie',
    'footer.community': 'Communauté',
    'footer.contact': 'Contact',
    'footer.teachContribute': 'Enseigner ou contribuer',
    'footer.school': 'OFALycée École',
    'footer.blog': 'Blog OFA',
    'perspectives.blog.title': 'Continuez l\'exploration sur notre blog',
    'perspectives.blog.subtitle': 'Pour des essais approfondis, témoignages d\'anciens et réflexions pédagogiques, découvrez notre blog complémentaire — au sein du même écosystème OFA Lycée™.',
    'perspectives.blog.button': 'Visiter le Blog OFA',
    'footer.studentPortal': 'Portail étudiant',
    'footer.bilingual': 'Bilingue EN / FR',
    'footer.globalOnline': 'Mondial — En ligne',
    'footer.rights': 'Tous droits réservés.',
    'footer.privacy': 'Politique de confidentialité',
    'footer.terms': 'Conditions d\'utilisation',
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (fromUrl === 'fr' || fromUrl === 'en') {
      localStorage.setItem('ofa_lang', fromUrl);
      return fromUrl;
    }
    return localStorage.getItem('ofa_lang') || 'en';
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    const url = new URL(window.location.href);
    if (url.searchParams.has('lang') && url.searchParams.get('lang') !== lang) {
      url.searchParams.set('lang', lang);
      window.history.replaceState(window.history.state, '', url);
    }
  }, [lang]);

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === 'en' ? 'fr' : 'en';
      localStorage.setItem('ofa_lang', next);
      return next;
    });
  }, []);

  const setLanguage = useCallback((l) => {
    setLang(l);
    localStorage.setItem('ofa_lang', l);
  }, []);

  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations['en']?.[key] || key;
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang: setLanguage, toggleLang, t }),
    [lang, setLanguage, toggleLang, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
